<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form ref="validationCheckForm" @submit="addValidationCheck" :validation-schema="validationCheckSchema">
        <div class="row">
          <!-- En-tête -->
          <div class="col-12 mb-3">
            <h6 class="text-primary fw-semibold border-bottom pb-2">
              <i class="ri-checkbox-circle-line"></i> Ajouter un élément de contrôle
            </h6>
          </div>

          <!-- Tâche de validation (obligatoire) -->
          <div class="col-md-12 mb-3">
            <div class="alert alert-info">
              <i class="ri-information-line"></i>
              <strong>Note :</strong> Ce contrôle sera attaché à une tâche de validation spécifique.
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Tâche de validation <span class="text-danger">*</span>
              </label>
              <Field name="validation_task_id" v-slot="{ field }">
                <Multiselect :options="validationTaskOptions" :searchable="true" :multiple="false" track-by="value"
                  label="label" v-model="field.value" v-bind="field"
                  placeholder="Sélectionner la tâche de validation" />
              </Field>
              <ErrorMessage name="validation_task_id" class="text-danger" />
            </div>
          </div>

          <!-- Code technique -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Code technique <span class="text-danger">*</span>
              </label>
              <Field name="check_code" v-slot="{ field }">
                <Multiselect :options="checkCodeOptions" :searchable="true" :multiple="false" :taggable="true"
                  :create-option="true" track-by="value" label="label" v-model="field.value" v-bind="field"
                  placeholder="Sélectionner ou saisir un code" />
              </Field>
              <ErrorMessage name="check_code" class="text-danger" />
              <small class="text-muted">Choisissez dans la liste ou saisissez un nouveau code</small>
            </div>
          </div>

          <!-- Titre -->
          <div class="col-md-12">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Titre du contrôle <span class="text-danger">*</span>
              </label>
              <Field name="title" type="text" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Ex: Facture commerciale fournie, Code HS cohérent" />
              <ErrorMessage name="title" class="text-danger" />
            </div>
          </div>

          <!-- Description -->
          <div class="col-md-12">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Description détaillée <span class="text-danger">*</span>
              </label>
              <Field name="description" as="textarea" rows="3" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Description détaillée du contrôle, telle qu'affichée à l'agent dans la check-list" />
              <ErrorMessage name="description" class="text-danger" />
            </div>
          </div>

          <!-- Type de contrôle -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Type de contrôle <span class="text-danger">*</span>
              </label>
              <Field name="check_type" v-slot="{ field }">
                <Multiselect :options="checkTypeOptions" :searchable="true" :multiple="false" track-by="value"
                  label="label" v-model="field.value" v-bind="field" placeholder="Sélectionner le type" />
              </Field>
              <ErrorMessage name="check_type" class="text-danger" />
            </div>
          </div>

          <!-- Résultat -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Résultat du contrôle <span class="text-danger">*</span>
              </label>
              <Field name="result" v-slot="{ field }">
                <Multiselect :options="resultOptions" :searchable="true" :multiple="false" track-by="value"
                  label="label" v-model="field.value" v-bind="field" placeholder="Sélectionner le résultat" />
              </Field>
              <ErrorMessage name="result" class="text-danger" />
            </div>
          </div>

          <!-- Sévérité -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Sévérité en cas d'échec <span class="text-danger">*</span>
              </label>
              <Field name="severity" v-slot="{ field }">
                <Multiselect :options="severityOptions" :searchable="true" :multiple="false" track-by="value"
                  label="label" v-model="field.value" v-bind="field" placeholder="Sélectionner la sévérité" />
              </Field>
              <ErrorMessage name="severity" class="text-danger" />
            </div>
          </div>



          <!-- Boutons d'action -->
          <div class="col-md-12">
            <div class="d-flex align-items-center">
              <button
                class="default-btn me-20 transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 rounded-1 fs-md-15 fs-lg-16 bg-success"
                type="submit">
                <i class="ri-save-line me-2"></i>
                Enregistrer le contrôle
              </button>
              <router-link to="/listevalidationchecks" class="btn btn-danger transition border-0 lh-1 fw-medium">
                <i class="ri-close-line me-2"></i>
                Annuler
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import ApiService from '../../services/ApiService';
import * as Yup from 'yup';
import { error, success } from '../../utils/utils';
import Multiselect from '@vueform/multiselect';
import JwtService from '@/services/JwtService';
import router from '@/router';

export default defineComponent({
  name: "AddValidationCheck",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect
  },
  setup() {
    // Schema de validation conforme au cahier des charges
    const validationCheckSchema = Yup.object().shape({
      validation_task_id: Yup.string().required('La tâche de validation est obligatoire'),
      check_code: Yup.string().required('Le code technique est obligatoire'),
      title: Yup.string().required('Le titre est obligatoire'),
      description: Yup.string().required('La description est obligatoire'),
      check_type: Yup.string().required('Le type de contrôle est obligatoire'),
      result: Yup.string().required('Le résultat est obligatoire'),
      severity: Yup.string().required('La sévérité est obligatoire'),
      // comments: Yup.string().nullable(), // Optionnel
    });


    const validationTaskOptions = ref([]);

    const checkTypeOptions = [
      { value: 'MANUAL', label: 'Manuel' }
    ];


    const resultOptions = [
      { value: 'PENDING', label: 'En attente' },
      { value: 'PASSED', label: 'Validé' },
      { value: 'FAILED', label: 'Échoué' },
      { value: 'NOT_APPLICABLE', label: 'Non applicable' }
    ];

    const severityOptions = [
      { value: 'INFO', label: ' Info' },
      { value: 'WARNING', label: 'Warning' },
      { value: 'BLOCKING', label: ' Bloquant' }
    ];


    // AJOUTER cette constante après les autres options (ligne ~120 environ)

    const checkCodeOptions = [
      // Documents
      { value: 'DOC_INVOICE_PRESENT', label: 'DOC_INVOICE_PRESENT - Facture commerciale présente' },
      { value: 'DOC_PACKING_LIST_PRESENT', label: 'DOC_PACKING_LIST_PRESENT - Liste de colisage présente' },
      { value: 'DOC_BOL_PRESENT', label: 'DOC_BOL_PRESENT - Connaissement présent' },
      { value: 'DOC_CERTIFICATE_ORIGIN', label: 'DOC_CERTIFICATE_ORIGIN - Certificat d\'origine présent' },
      { value: 'DOC_PHYTOSANITARY_CERT', label: 'DOC_PHYTOSANITARY_CERT - Certificat phytosanitaire' },

      // Cohérence des données
      { value: 'DATA_HS_CODE_VALID', label: 'DATA_HS_CODE_VALID - Code HS valide' },
      { value: 'DATA_VALUE_MATCHES_INVOICE', label: 'DATA_VALUE_MATCHES_INVOICE - Valeur cohérente avec facture' },
      { value: 'DATA_WEIGHT_MATCHES_BL', label: 'DATA_WEIGHT_MATCHES_BL - Poids cohérent avec BL' },
      { value: 'DATA_QUANTITY_MATCHES', label: 'DATA_QUANTITY_MATCHES - Quantité cohérente' },
      { value: 'DATA_ORIGIN_COUNTRY_VALID', label: 'DATA_ORIGIN_COUNTRY_VALID - Pays d\'origine valide' },

      // Conformité réglementaire
      { value: 'REG_RESTRICTED_GOODS_CHECK', label: 'REG_RESTRICTED_GOODS_CHECK - Marchandise non restreinte' },
      { value: 'REG_IMPORT_LICENSE_VALID', label: 'REG_IMPORT_LICENSE_VALID - Licence d\'import valide' },
      { value: 'REG_CUSTOMS_VALUE_ACCEPTABLE', label: 'REG_CUSTOMS_VALUE_ACCEPTABLE - Valeur en douane acceptable' },
      { value: 'REG_TARIFF_CLASSIFICATION', label: 'REG_TARIFF_CLASSIFICATION - Classification tarifaire correcte' },

      // Analyse de risque
      { value: 'RISK_SHIPPER_PROFILE', label: 'RISK_SHIPPER_PROFILE - Profil expéditeur vérifié' },
      { value: 'RISK_CONSIGNEE_PROFILE', label: 'RISK_CONSIGNEE_PROFILE - Profil destinataire vérifié' },
      { value: 'RISK_VALUE_ANOMALY', label: 'RISK_VALUE_ANOMALY - Anomalie de valeur détectée' },
      { value: 'RISK_ROUTE_ANOMALY', label: 'RISK_ROUTE_ANOMALY - Anomalie de route détectée' },

      // Contrôles douaniers
      { value: 'CUSTOMS_DUTY_CALCULATED', label: 'CUSTOMS_DUTY_CALCULATED - Droits de douane calculés' },
      { value: 'CUSTOMS_VAT_CALCULATED', label: 'CUSTOMS_VAT_CALCULATED - TVA calculée' },
      { value: 'CUSTOMS_PAYMENT_VERIFIED', label: 'CUSTOMS_PAYMENT_VERIFIED - Paiement vérifié' }
    ];





    // Récupération des tâches de validation
    const fetchValidationTask = async () => {
      try {
        const user = JwtService.getUser();
        if (!user) {
          error("Utilisateur non connecté");
          return;
        }

        const userId = user.userId?.replace(/\/+$/, '').trim();
        if (!userId) {
          error("Utilisateur non connecté");
          return;
        }

        const response = await ApiService.get(`/validationts?limit=0&userCreationId=${userId}`);
        const validationTaskData = response.data.data.data;

        validationTaskOptions.value = validationTaskData.map((task) => ({
          value: task.id,
          label: `Tâche #${task.id} - ${task.besc_file?.reference || 'N/A'} - ${task.status}`
        }));

      } catch (err) {
        console.error("Erreur lors de la récupération des tâches:", err);
        error("Impossible de charger les tâches de validation");
      }
    };

    const addValidationCheck = async (values: any, { resetForm }) => {
      try {
        
        const payload = {
          validation_task_id: values.validation_task_id,
          check_code: values.check_code,
          title: values.title,
          description: values.description,
          check_type: values.check_type,
          result: values.result,
          severity: values.severity,
          comments: values.comments || null,
        };

        console.log("Données envoyées:", payload);

        const response = await ApiService.post("/validationc", payload);

        if (response.data.code === 201) {
          success(response.data.message);
          resetForm();
          router.push({ name: "ListeValidationCheckPage" });
        }

      } catch (err: any) {
        console.error("Erreur lors de la création:", err);
        error(err.response?.data?.message || "Erreur lors de la création du contrôle");
      }
    };

    onMounted(() => {
      fetchValidationTask();
    });

    return {
      validationCheckSchema,
      addValidationCheck,
      validationTaskOptions,
      checkTypeOptions,
      resultOptions,
      severityOptions,
      checkCodeOptions
    };
  },
});
</script>

<style scoped>
.alert-info {
  background-color: #e7f3ff;
  border-color: #b3d9ff;
  color: #004085;
}

small.text-muted {
  font-size: 0.875rem;
  display: block;
  margin-top: 0.25rem;
}
</style>